import React, { useState, useRef, useEffect, useCallback } from "react";
import { Tensor, InferenceSession } from "onnxruntime-web";

import Loader from "./components/loader";
import { detectImage } from "./utils/detect";
import { download } from "./utils/download";

import "./style/App.css";

/* -----------------------------
 M ODEL CONFIG       *
 ----------------------------- */

const modelName = "Petriplate.onnx";
const modelInputShape = [1, 3, 640, 640];

const topk = 1000;
const iouThreshold = 0.25;
const scoreThreshold = 0.1;

/* -----------------------------
 A PP COMPONENT      *
 ----------------------------- */

const App = () => {

  const [session, setSession] = useState(null);
  const [image, setImage] = useState(null);
  const [stats, setStats] = useState(null);
  const [showLabels, setShowLabels] = useState(true);

  const [loading, setLoading] = useState({
    text: "Loading model...",
    progress: null
  });

  const inputImage = useRef(null);
  const imageRef = useRef(null);
  const canvasRef = useRef(null);

  /* -----------------------------
   I NITIALIZE MODEL *
   ----------------------------- */

  const initModel = useCallback(async () => {

    const baseModelURL = `${process.env.PUBLIC_URL}/model`;

    const arrBufNet = await download(
      `${baseModelURL}/${modelName}`,
      ["Loading Model", setLoading]
    );

    const yolov8 = await InferenceSession.create(arrBufNet, {
      executionProviders: ["wasm"]
    });

    const arrBufNMS = await download(
      `${baseModelURL}/nms-yolov8.onnx`,
      ["Loading NMS", setLoading]
    );

    const nms = await InferenceSession.create(arrBufNMS);

    setLoading({
      text: "Warming up model...",
      progress: null
    });

    const tensor = new Tensor(
      "float32",
      new Float32Array(modelInputShape.reduce((a, b) => a * b)),
                              modelInputShape
    );

    await yolov8.run({ images: tensor });

    setSession({
      net: yolov8,
      nms
    });

    setLoading(null);

  }, []);

  useEffect(() => {
    initModel();
  }, [initModel]);

  /* -----------------------------
   C LEANUP IMAGE MEM*ORY
   ----------------------------- */

  useEffect(() => {
    return () => {
      if (image) URL.revokeObjectURL(image);
    };
  }, [image]);

  /* -----------------------------
   R EDRAW WHEN TOGGL*E CHANGES
   ----------------------------- */

  useEffect(() => {

    if (!session || !image) return;

    const img = imageRef.current;
    const canvas = canvasRef.current;

    if (!img || !canvas) return;

    canvas.width = img.clientWidth;
    canvas.height = img.clientHeight;

    if (!showLabels) {
      const ctx = canvas.getContext("2d");
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      return;
    }

    detectImage(
      img,
      canvas,
      session,
      topk,
      iouThreshold,
      scoreThreshold,
      modelInputShape,
      setStats,
      showLabels
    );

  }, [showLabels, session, image]);

  /* -----------------------------
   U I               *
   ----------------------------- */

  return (
    <div className="app-container">

    {loading && (
      <Loader>
      {loading.progress
        ? `${loading.text} - ${loading.progress}%`
        : loading.text}
        </Loader>
    )}

    {/* Header */}

    <header className="app-header">

    <div className="logo">
    🧫 Petri Plate Counter
    </div>

    <div className="header-actions">

    <button
    className="upload-btn"
    onClick={() => inputImage.current.click()}
    >
    Upload Image
    </button>

    {image && (
      <button
      className="remove-btn"
      onClick={() => {

        inputImage.current.value = "";
        imageRef.current.src = "#";

        URL.revokeObjectURL(image);

        setImage(null);
        setStats(null);

      }}
      >
      Remove
      </button>
    )}

    </div>

    </header>


    {/* Workspace */}

    <div className="workspace">

    {/* Image Viewer */}

    <div className="image-viewer">

    <img
    ref={imageRef}
    src="#"
    alt=""
    style={{ display: image ? "block" : "none" }}
    onLoad={() => {

      if (!session) return;

      const img = imageRef.current;
      const canvas = canvasRef.current;

      canvas.width = img.clientWidth;
      canvas.height = img.clientHeight;

      detectImage(
        img,
        canvas,
        session,
        topk,
        iouThreshold,
        scoreThreshold,
        modelInputShape,
        setStats,
        showLabels
      );

    }}
    />

    <canvas ref={canvasRef}></canvas>

    </div>


    {/* Analysis Panel */}

    <aside className="analysis-panel">

    <h2>Analysis</h2>

    {stats ? (

      <div className="stats">

      <div className="stat-card">
      <span>Colonies</span>
      <strong>{stats.count}</strong>
      </div>

      <div className="stat-card">
      <span>Avg Diameter</span>
      <strong>{stats.avgDiameter.toFixed(2)} px</strong>
      </div>

      <div className="stat-card">
      <span>Min Diameter</span>
      <strong>{stats.minDiameter.toFixed(2)} px</strong>
      </div>

      <div className="stat-card">
      <span>Max Diameter</span>
      <strong>{stats.maxDiameter.toFixed(2)} px</strong>
      </div>

      </div>

    ) : (

      <p className="placeholder">
      Upload a petri plate image to begin analysis
      </p>

    )}

    {/* Label Toggle */}

    {image && (
      <div className="toggle-container">
      <label>
      <input
      type="checkbox"
      checked={showLabels}
      onChange={(e) => setShowLabels(e.target.checked)}
      />
      Show Labels
      </label>
      </div>
    )}

    </aside>

    </div>


    {/* Hidden File Input */}

    <input
    type="file"
    ref={inputImage}
    accept="image/*"
    style={{ display: "none" }}
    onChange={(e) => {

      const file = e.target.files[0];
      if (!file) return;

      if (image) URL.revokeObjectURL(image);

      const url = URL.createObjectURL(file);

      imageRef.current.src = url;

      setStats(null);
      setImage(url);

    }}
    />

    </div>
  );
};

export default App;
